import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
} from '@mui/material';
import React, {useRef} from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrBarStripe, {GraphDataItem} from '../arr-bar-stripe/ArrBarStripe';
import './ArrDocumentTile.scss';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import ArrMenu, {ArrMenuHandle, CustomMenuItem} from '../arr-menu/ArrMenu';
import {useTranslation} from 'react-i18next';

type MenuDirection =
  | 'bottom'
  | 'top'
  | 'left'
  | 'right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right';

interface DocumentTileProps {
  primaryLabel?: string;
  secondaryLabel?: string;
  tertiaryLabel?: string;
  primaryLabelColor?: string;
  secondaryLabelColor?: string;
  tertiaryLabelColor?: string;
  status?: string;
  lastModifiedBy?: string;
  lastModifiedTimestamp?: string;
  statusTimestamp?: string;
  metaDataList?: ITileMetadataItem[];
  bates?: IBates;
  actionMenuItems?: CustomMenuItem[];
  progressBarProps: IProgressBarProps;
  menuDirection?: MenuDirection;
  dataTestId: string;
}

export interface ITileMetadataItem {
  label: string;
  value: string | number | boolean;
}

export interface IBates {
  startNumber?: string;
  endNumber?: string;
}

export interface IProgressBarProps {
  total?: string;
  graphData?: Array<GraphDataItem>;
  stripeWidth?: number;
  gap?: number;
  height?: number;
  // Optional percent variant
  percent?: number; // 0-100
  state?: 'in-progress' | 'success' | 'failed' | 'draft' | 'completed';
}

const DocumentTile: React.FC<DocumentTileProps> = ({
  primaryLabel,
  secondaryLabel,
  tertiaryLabel,
  primaryLabelColor,
  secondaryLabelColor,
  tertiaryLabelColor,
  status,
  lastModifiedBy,
  lastModifiedTimestamp,
  statusTimestamp,
  metaDataList,
  bates,
  actionMenuItems = [],
  progressBarProps,
  menuDirection = 'bottom-left',
  dataTestId = '',
}) => {
  const {t} = useTranslation();
  const menuRef1 = useRef<ArrMenuHandle | null>(null);
  const onButtonClick1 = (e: React.MouseEvent<HTMLElement>) => {
    menuRef1.current?.open(e.currentTarget as HTMLElement);
  };
  const onItemClick = (item: CustomMenuItem) => {
    item.onClick?.();
  };

  const handlePrimaryLabelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const wizardItem = actionMenuItems.find(
      (x) => x.label === 'Open in Wizard'
    );

    wizardItem?.onClick?.();
  };
  return (
    <Card component="article" elevation={0} className="arr-feature-card">
      <CardHeader
        className="arr-feature-card__header"
        disableTypography
        title={
          <>
            {secondaryLabelColor && (
              <Typography
                className={`arr-feature-card__label--secondary typo-text label3_size text-ellipsis ${secondaryLabelColor || 'text-headings'}`}
                data-testid={`arr-document-tile-sec-label-${dataTestId}`}
              >
                {secondaryLabel}
              </Typography>
            )}
            {primaryLabel && (
              <Typography
                onClick={handlePrimaryLabelClick || undefined}
                component="h3"
                variant="body1"
                style={{cursor: 'pointer'}}
                className={`arr-feature-card__label--primary typo-text subhead4_size text-ellipsis ${primaryLabelColor || 'text-headings'}`}
                data-testid={`arr-document-tile-primary-label-${primaryLabel}`}
              >
                {primaryLabel}
              </Typography>
            )}
            {tertiaryLabel && (
              <Typography
                role="output"
                aria-label={`Status: ${tertiaryLabel}`}
                className={`arr-feature-card__label--tertiary typo-text subhead5_size text-ellipsis ${tertiaryLabelColor || 'text-headings'}`}
                data-testid={`arr-document-tile-tertiary-label-${dataTestId}`}
              >
                {tertiaryLabel}
              </Typography>
            )}
          </>
        }
        action={
          actionMenuItems.length ? (
            <>
              <ArrMenu
                id="arr-menu-1"
                ref={menuRef1}
                items={actionMenuItems}
                onItemClick={onItemClick}
                direction={menuDirection}
              />
              <ArrIconButton
                onClick={onButtonClick1}
                size="medium"
                shape="default"
                icon={<MoreVertIcon />}
                ariaLabel="menu-icon"
                testId="arr-document-tile-menu-icon-icon-button"
              />
            </>
          ) : null
        }
      ></CardHeader>
      <CardContent>
        <Typography
          role="output"
          aria-label={`Status: ${status}`}
          className="arr-feature-card__label--progress-status typo-text label3_size"
          data-testid={`arr-document-tile-status-${dataTestId}`}
        >
          {status}
        </Typography>
        <Box>
          {(() => {
            const normalizeState = (
              raw?: string
            ): 'in-progress' | 'success' | 'failed' | 'draft' | 'completed' => {
              const s = (raw || '').toLowerCase().replaceAll(/[\s_]/g, '');
              if (s.includes('progress')) return 'in-progress';
              if (s.includes('fail')) return 'failed';
              if (s.includes('completed') || s.includes('complete'))
                return 'completed';
              if (s.includes('success')) return 'success';
              return 'draft';
            };

            const derivedState = normalizeState(
              progressBarProps?.state || status
            );
            const isPercentMode = typeof progressBarProps?.percent === 'number';

            return (
              <ArrBarStripe
                height={progressBarProps?.height}
                total={progressBarProps?.total || '100'}
                mode={isPercentMode ? 'percent' : 'graph'}
                percent={progressBarProps?.percent}
                state={derivedState}
                stripeWidth={
                  isPercentMode ? 2 : progressBarProps?.stripeWidth || 2
                }
                gap={isPercentMode ? 0.8 : progressBarProps?.gap || 2}
                dataTestId="arr-document-tile-progress-bar"
              ></ArrBarStripe>
            );
          })()}
        </Box>
        <Box className="arr-feature-card__content">
          <>
            {metaDataList?.map((data, idx) => (
              <Box
                className="arr-feature-card__meta-data-box"
                key={data.label}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
              >
                <Typography
                  className="typo-text label3_size text-body-subtle"
                  data-testid={`arr-document-tile-label-meta-${idx}-${dataTestId}`}
                >
                  {data.label}
                </Typography>
                <Typography
                  className="typo-text subhead2_size text-headings"
                  data-testid={`arr-document-tile-value-meta-${idx}-${dataTestId}`}
                >
                  {data.value}
                </Typography>
              </Box>
            ))}
            {bates && (
              <Box className="arr-feature-card__bates">
                <Typography
                  className="typo-text label3_size text-body-subtle"
                  data-testid={`arr-document-tile-bates-title`}
                >
                  {t('common.documentTile.bates.title')}
                </Typography>
                <Box className="arr-feature-card__bates-box">
                  <Typography
                    className="typo-text buttonXS_size text-body-subtle"
                    data-testid={`arr-document-tile-start`}
                  >
                    {t('common.documentTile.bates.start')}
                  </Typography>
                  <Typography
                    className="typo-text buttonXS_size text-headings text-ellipsis-start"
                    data-testid={`arr-document-tile-bates-start-number-${dataTestId}`}
                  >
                    {bates.startNumber || '\u00A0'}
                  </Typography>
                </Box>
                <Box className="arr-feature-card__bates-box">
                  <Typography
                    className="typo-text buttonXS_size text-body-subtle"
                    data-testid={`arr-document-tile-end`}
                  >
                    {t('common.documentTile.bates.end')}
                  </Typography>
                  <Typography
                    className="typo-text buttonXS_size text-headings text-ellipsis-start"
                    data-testid={`arr-document-tile-bates-end-number-${dataTestId}`}
                  >
                    {bates.endNumber || '\u00A0'}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        </Box>
        <Divider></Divider>
        <Box className="arr-feature-card__last-modified">
          <Typography
            className="typo-text label3_size text-body-subtle"
            data-testid={`arr-document-tile-last-modified`}
          >
            {t('common.documentTile.lastModified')}
          </Typography>
          <Typography
            className="typo-text body4_size text-headings"
            data-testid={`arr-document-tile-last-modified-${dataTestId}`}
          >
            {lastModifiedBy + ' ' + lastModifiedTimestamp}
          </Typography>
        </Box>
        <Box>
          <Typography
            className="typo-text label3_size text-body-subtle"
            data-testid={`arr-document-tile-last-status`}
          >
            {t('common.documentTile.lastStatus')}
          </Typography>
          <Typography
            className="typo-text body4_size text-headings"
            data-testid={`arr-document-tile-status-timestamp-${dataTestId}`}
          >
            {statusTimestamp}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DocumentTile;
